import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { cartApi, type Cart } from "../../api/cart";
import { errorMessage } from "../account/session";

export const CART_KEY = ["cart"] as const;

/** The basket, priced for whoever is looking. Refetched on sign-in, when the server merges it. */
export function useCart() {
  return useQuery({ queryKey: CART_KEY, queryFn: cartApi.get });
}

/** Adds a part. The answer is the whole basket, so nothing is refetched. */
export function useAddToCart() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listingId, quantity }: { listingId: number; quantity: number }) => cartApi.add(listingId, quantity),
    onSuccess: (cart: Cart) => queryClient.setQueryData(CART_KEY, cart),
  });
}

/** An add-to-basket control's state, for one listing at a time. */
export function useAddToBasket(listingId: number) {
  const add = useAddToCart();
  const [added, setAdded] = useState<number | null>(null);
  return {
    onAdd: (quantity: number) => {
      setAdded(null);
      add.mutate({ listingId, quantity }, { onSuccess: () => setAdded(quantity) });
    },
    state: { adding: add.isPending, added, error: errorMessage(add.error) },
  };
}
